import { useCallback } from 'react';

import { useFocusEffect } from 'expo-router';
import { useIntl } from 'react-intl';
import { Image, StyleSheet, Text, View } from 'react-native';

import { IQuest } from 'components/quests/loyalty-quest/types';
import { LoyaltyQuestStatus, LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive } from 'styles/constants/primitives';
import { WEB_EXTRA_SMALL_MAX_WIDTH, WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { isMobile } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { ChallengeProgressCard } from './challenge-progress-card';
import { ChallengeProgressCardSkeleton } from './challenge-progress-card-skeleton';

const icon = require('./section-icon.png');

const getTotalCrowns = (challenge: IQuest) => {
  return challenge.steps.reduce((acm, step) => (step.bonusPoints || 0) + acm, 0);
};

const getCompletedStepsCount = (challenge: IQuest) => {
  return challenge.steps.filter(step => step.status === LoyaltyQuestStepStatus.COMPLETED).length;
};

const getCompletedUserJourneySeen = () => {
  return LocalStorage.getItem(StorageKeys.COMPLETED_USER_JOURNEY_SEEN) || {};
};

export const UserJourneyChallenge = ({
  loading,
  challenge,
}: {
  loading?: boolean;
  challenge?: IQuest;
}) => {
  const { formatMessage } = useIntl();
  const { loyaltyUserId } = useLoyaltyUserId();

  // Hide the profile completion section once the challenge is completed and the user has already seen it once
  useFocusEffect(
    useCallback(() => {
      return () => {
        if (loyaltyUserId && challenge?.status === LoyaltyQuestStatus.COMPLETED) {
          LocalStorage.setItem(StorageKeys.COMPLETED_USER_JOURNEY_SEEN, {
            ...getCompletedUserJourneySeen(),
            [loyaltyUserId]: true,
          });
        }
      };
    }, [loyaltyUserId, challenge?.status])
  );
  if (getCompletedUserJourneySeen()[loyaltyUserId]) {
    return null;
  }

  return (
    <View style={styles.wrapper}>
      <View style={styles.title}>
        <Image style={styles.titleIcon} source={icon} />
        <Text style={styles.titleText}>
          {formatMessage({ id: 'profileCompletion', defaultMessage: 'Profile Completion' })}
        </Text>
      </View>
      <View>
        {loading && <ChallengeProgressCardSkeleton />}
        {!loading && challenge && (
          <ChallengeProgressCard
            crownsAmount={getTotalCrowns(challenge)}
            challengeStatus={challenge.status}
            completedSteps={getCompletedStepsCount(challenge)}
            totalSteps={challenge.steps.length}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    gap: isMobile() ? primitive.spacing.$3 : primitive.spacing.$4,
    paddingHorizontal: primitive.spacing.$4,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    width: '100%',
    ...(!isMobile() && {
      maxWidth: WEB_EXTRA_SMALL_MAX_WIDTH,
      boxSizing: 'content-box',
    }),
  },
  title: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  titleText: {
    fontSize: primitive.fontSizes.lg,
    fontFamily: primitive.fontFamily.$headers,
    alignItems: 'flex-end',
    display: 'flex',
    color: primitive.bk.$bbqBrown,
  },
  titleIcon: {
    width: 24,
    height: 24,
  },
});
